import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Divider,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

/**
 * Reusable form dialog component for consistent modal forms across the application
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {string} props.title - Dialog title
 * @param {React.ReactNode} props.children - Dialog content (typically a form)
 * @param {React.ReactNode} props.actions - Custom action buttons
 * @param {boolean} props.loading - Whether the form is submitting
 * @param {function} props.onSubmit - Function to call when primary action button is clicked
 * @param {string} props.submitLabel - Label for the submit button
 * @param {boolean} props.fullWidth - Whether the dialog should take up the full width
 * @param {string} props.maxWidth - Maximum width of the dialog (xs, sm, md, lg, xl)
 */
const FormDialog = ({
  open,
  onClose,
  title,
  children,
  actions,
  loading = false,
  onSubmit,
  submitLabel = 'Save',
  fullWidth = true,
  maxWidth = 'sm'
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      aria-labelledby="form-dialog-title"
    >
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle id="form-dialog-title">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{title}</Typography>
            {!loading && (
              <IconButton
                aria-label="close"
                onClick={onClose}
                size="small"
                edge="end"
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {children}
        </DialogContent>
        <Divider />
        <DialogActions>
          {actions ? (
            actions
          ) : (
            <>
              <Button 
                onClick={onClose} 
                disabled={loading}
                color="inherit"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {loading ? 'Saving...' : submitLabel}
              </Button>
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default FormDialog;
